import React, { useEffect } from 'react'
import * as Styled from './styled'
import { translate } from 'utils/translations'
import RecentlyUsedImage from './ImageItem'
import { IBaseMediaItem } from 'types/media'
import { useSelector } from 'react-redux'
import RecentlyUsedVideo from './VideoItem'
import { convertBaseItemToMediaItem } from 'utils/media'
import { DEFAULT_MEDIA_DURATION, imageTypes } from 'consts'
import { scrollToElementById } from 'utils'
import useDurationLimitCheck from 'hooks/useDurationLimitCheck'
import { userSelectors } from 'store/features/user/selectors'
import { projectDataThunks } from 'store/features/projectData/thunks'
import { projectDataSelectors } from 'store/features/projectData/selectors'
import { T } from 'consts/translationKeys'
import { dispatch } from 'store'
import { recentlyUsedMediaThunks } from 'store/features/recentlyUsedMedia/thunks'
import { recentlyUsedMediaSelectors } from 'store/features/recentlyUsedMedia/selectors'
import { editorThunks } from 'store/features/editor/thunks'
import { Text } from 'common-components'
import { useVisualMediaPlacement } from 'hooks/timeline/useVisualMediaPlacement'
import { useSidebarItemDrag } from 'hooks/useSidebarItemDrag'
import { videoThumbsService } from 'services/video-thumbs'

const getCachedThumbIfExists = (assetId?: string) => {
  // checks for first frame in inedexdb
  const cached = assetId ? videoThumbsService.getCachedUrls(assetId) : null
  return cached ? cached.get(0) : undefined
}
const DraggableRecentlyUsedImage: React.FC<{
  item: IBaseMediaItem
  onClick: () => void
}> = ({ item, onClick }) => {
  const { setNodeRef, listeners, attributes, style } = useSidebarItemDrag(item)
  return (
    <RecentlyUsedImage
      ref={setNodeRef}
      src={item.path ?? undefined}
      onClick={onClick}
      style={style}
      {...listeners}
      {...attributes}
    />
  )
}

const DraggableRecentlyUsedVideo: React.FC<{
  item: IBaseMediaItem
  onClick: () => void
}> = ({ item, onClick }) => {
  const thumbnail = getCachedThumbIfExists(item.assetId)

  const { setNodeRef, listeners, attributes, style } = useSidebarItemDrag({
    ...item,
    thumbnail
  })

  return (
    <RecentlyUsedVideo
      ref={setNodeRef}
      src={item.path ?? undefined}
      duration={item.duration}
      fileName={item.fileName}
      onClick={onClick}
      style={style}
      {...listeners}
      {...attributes}
    />
  )
}

const RecentlyUsed: React.FC = () => {
  const userData = useSelector(userSelectors.data)
  const userId = userData?.id
  const { width: projectDefaultWidth, height: projectDefaultHeight } =
    useSelector(projectDataSelectors.defaultDimensions)
  const recentlyUsedItems = useSelector(
    recentlyUsedMediaSelectors.externalMediaItems
  )
  const { isDurationAllowed } = useDurationLimitCheck()
  const getPlacement = useVisualMediaPlacement()

  useEffect(() => {
    if (userId) {
      dispatch(recentlyUsedMediaThunks.filterAndUpdateRecentlyUsedItems())
    }
  }, [userId])

  const handleAddMediaFromRecents = (item: IBaseMediaItem) => () => {
    const totalNewDuration = item.duration || DEFAULT_MEDIA_DURATION

    if (!isDurationAllowed(totalNewDuration)) {
      dispatch(editorThunks.setIsMobileSubSidebarOpen(false))
      return
    }

    const placement = getPlacement(totalNewDuration)

    const preparedItem = convertBaseItemToMediaItem(
      item,
      placement.startTime,
      {
        width: projectDefaultWidth,
        height: projectDefaultHeight
      },
      placement.zIndex
    )

    dispatch(
      projectDataThunks.addMediaItems({
        value: [preparedItem],
        config: { applySelection: false }
      })
    )
    dispatch(
      editorThunks.setActiveTimelineItems([
        {
          id: preparedItem.id,
          type: preparedItem.type
        }
      ])
    )
    dispatch(editorThunks.setIsMobileSubSidebarOpen(false))
    scrollToElementById(preparedItem.id)
  }

  if (recentlyUsedItems.length === 0) {
    return null
  }

  return (
    <Styled.RecentlyUsedContainer>
      <Text type="h2" fontWeight="600" color="secondarySemiDark">
        {translate(T.RECENT_MEDIAS_LABEL)}
      </Text>
      <Styled.RecentlyUsedItemsList>
        {recentlyUsedItems.map((item: IBaseMediaItem) => {
          const key = `${item.path}-${item.mediaId}`
          return imageTypes.includes(item.type) ? (
            <DraggableRecentlyUsedImage
              key={key}
              item={item}
              onClick={handleAddMediaFromRecents(item)}
            />
          ) : (
            <DraggableRecentlyUsedVideo
              key={key}
              item={item}
              onClick={handleAddMediaFromRecents(item)}
            />
          )
        })}
      </Styled.RecentlyUsedItemsList>
    </Styled.RecentlyUsedContainer>
  )
}

export default RecentlyUsed
